import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Palette } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminContentThemes = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/content/themes');
      if (response.data.success) {
        // Transform is_active (0/1) to isActive (boolean)
        const transformedThemes = (response.data.themes || []).map(theme => ({
          ...theme,
          isActive: theme.is_active === 1 || theme.is_active === true || theme.isActive === true
        }));
        setThemes(transformedThemes);
      }
    } catch (error) {
      toast.error('Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedTheme(null);
    setFormData({ name: '', description: '', isActive: true });
    setShowModal(true);
  };

  const openEditModal = (theme) => {
    setSelectedTheme(theme);
    const isActive = theme.is_active === 1 || theme.is_active === true || theme.isActive === true;
    setFormData({
      name: theme.name || '',
      description: theme.description || '',
      isActive: isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Theme name is required');
      return;
    }

    try {
      setSaving(true);
      const url = selectedTheme 
        ? `/admin/content/themes/${selectedTheme.id}`
        : '/admin/content/themes';
      const method = selectedTheme ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      if (response.data.success) {
        toast.success(selectedTheme ? 'Theme updated successfully' : 'Theme created successfully');
        setShowModal(false);
        fetchThemes();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (theme) => {
    setSelectedTheme(theme);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/content/themes/${selectedTheme.id}`);
      if (response.data.success) {
        toast.success('Theme deleted successfully');
        setShowDeleteModal(false);
        fetchThemes();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete theme');
    } finally {
      setDeleting(false);
    }
  };

  const filteredThemes = themes.filter(theme =>
    theme.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminSidebar>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
            Story Themes
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Manage story themes for personalized stories</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
        >
          <Plus className="h-5 w-5" />
          Add Theme
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search themes..."
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredThemes.length === 0 ? (
        <EmptyState
          icon={<Palette className="h-7 w-7" />}
          title={search ? `No themes found for "${search}"` : 'No themes yet'}
          description={search ? 'Try adjusting your search terms' : 'Create your first story theme to get started'}
          action={search ? null : { label: 'Add Theme', onClick: openAddModal }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredThemes.map((theme) => (
            <div
              key={theme.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{theme.name}</h3>
                  {theme.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{theme.description}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    theme.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {theme.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(theme)}
                  className="flex-1 px-3 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                >
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => openDeleteConfirm(theme)}
                  className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal
          title={selectedTheme ? 'Edit Theme' : 'Add Theme'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Theme Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Adventure, Fantasy, Science"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                rows="3"
                placeholder="Theme description..."
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {formData.isActive ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedTheme ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && selectedTheme && (
        <ConfirmDialog
          title="Delete Theme"
          description={`Are you sure you want to delete "${selectedTheme.name}"? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedTheme(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminContentThemes;

